// src/hooks/useDashboardChat.ts
//
// Client-side driver for POST /dashboard/chat. Owns conversation state,
// pending-approval state, and the approve/decline/edit round-trip.
// Deliberately does NOT own message persistence beyond what the backend
// already returns — chatId/conversation are the source of truth,
// mirrored into local state for rendering.
//
// chats/refreshChats/startNewChat: added for the chat-history Drawer +
// New chat control in ChatScreen.tsx. refreshChats hits the existing
// GET /chats route (already ordered by updatedAt desc server-side —
// chats[0] is always the most recent, no client-side sort needed).
// startNewChat is purely local state reset — no backend call, since
// chat.ts already mints a fresh chatId server-side whenever chatId is
// omitted from a request (chatId || chatsRef.doc().id).
//
// onboarding mode — NEW: mirrors the 'owner' | 'counterparty' pattern.
// entityEstablished flips true the first time a chat response reports
// the entity now exists (data.entityEstablished from the backend).
// DashboardShell polls nothing — it just watches this flag via the
// hook instance it owns during onboarding and swaps UI on change.
// Assumes /dashboard/chat starts returning an `entityEstablished:
// boolean` field on ChatResponse once mode: 'onboarding' is sent —
// if the backend doesn't emit this yet, that's a corresponding
// server-side change this hook now depends on, not something this
// file can fake convincingly on its own.

import { useState, useCallback } from 'react';
import { authedFetchRaw } from '../services/dashboardApi';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: any;
}

export interface PendingApproval {
  tool_use_id: string;
  name: string;
  input: any;
}

export interface ChatSummary {
  id: string;
  title: string;
  updatedAt: string | null;
}

export interface LoadedChat {
  chatId: string;
  conversation: ChatMessage[];
  targetEntityId: string | null;
}

interface ChatResponse {
  success: boolean;
  reply: string;
  toolCalls: Array<{ tool: string; input: any; result: any }>;
  pendingApproval: PendingApproval | null;
  conversation: ChatMessage[];
  chatId: string;
  usage: { inputTokens: number; outputTokens: number };
  entityEstablished?: boolean; // NEW — only meaningful when mode: 'onboarding'
}

export function useDashboardChat(
  initialChatId?: string,
  // 'owner' — ChatScreen.tsx, ongoing use, targets caller's own entity.
  // 'counterparty' — RegistryView.tsx contact chat, requires targetEntityId.
  // 'onboarding' — NEW: first-run entity/MoR setup, same wire format as
  // 'owner' but signals the backend to run the onboarding system prompt
  // and to report entityEstablished once the entity-creation tool call
  // resolves.
  options?: { mode?: 'owner' | 'counterparty' | 'onboarding'; targetEntityId?: string }
) {
  const mode = options?.mode ?? 'owner';
  const targetEntityId = options?.targetEntityId;

  const [conversation, setConversation] = useState<ChatMessage[]>([]);
  const [chatId, setChatId] = useState<string | undefined>(initialChatId);
  const [pendingApproval, setPendingApproval] = useState<PendingApproval | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [entityEstablished, setEntityEstablished] = useState(false);

  const sendMessage = useCallback(async (text: string) => {
    setIsLoading(true);
    setError(null);
    const nextMessages: ChatMessage[] = [...conversation, { role: 'user', content: text }];
    setConversation(nextMessages);

    try {
      const res = await authedFetchRaw('/dashboard/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: nextMessages,
          chatId,
          mode,
          ...(mode === 'counterparty' ? { targetEntityId } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Chat request failed.');
      }
      const data: ChatResponse = await res.json();
      setConversation(data.conversation);
      setChatId(data.chatId);
      setPendingApproval(data.pendingApproval);
      if (mode === 'onboarding' && data.entityEstablished) {
        setEntityEstablished(true);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  }, [conversation, chatId, mode, targetEntityId]);

  const resolveApproval = useCallback(async (approved: boolean, editedInput?: any) => {
    if (!pendingApproval) return;
    setIsLoading(true);
    setError(null);

    try {
      const res = await authedFetchRaw('/dashboard/chat', {
        method: 'POST',
        body: JSON.stringify({
          messages: conversation,
          chatId,
          mode,
          ...(mode === 'counterparty' ? { targetEntityId } : {}),
          approvalDecision: { tool_use_id: pendingApproval.tool_use_id, approved, editedInput },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to resolve approval.');
      }
      const data: ChatResponse = await res.json();
      setConversation(data.conversation);
      setChatId(data.chatId);
      setPendingApproval(data.pendingApproval);
      if (mode === 'onboarding' && data.entityEstablished) {
        setEntityEstablished(true);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  }, [conversation, chatId, pendingApproval, mode, targetEntityId]);

  const loadChat = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetchRaw(`/dashboard/chat/chats/${id}`);
      if (!res.ok) throw new Error('Failed to load chat.');
      const data = await res.json();
      setConversation(data.conversation);
      setChatId(data.chatId);
      setPendingApproval(data.pendingApproval ?? null);
      return data.targetEntityId ?? null;
    } catch (err: any) {
      setError(err.message || 'Could not load this conversation.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshChats = useCallback(async () => {
    try {
      const res = await authedFetchRaw('/dashboard/chat/chats');
      if (!res.ok) return;
      const data = await res.json();
      setChats(data.chats ?? []);
    } catch {
      // Non-fatal — history list failing to load shouldn't block chat itself.
    }
  }, []);

  const startNewChat = useCallback(() => {
    setConversation([]);
    setChatId(undefined);
    setPendingApproval(null);
    setError(null);
  }, []);

  return {
    conversation, chatId, pendingApproval, isLoading, error,
    chats, refreshChats, startNewChat,
    sendMessage, resolveApproval, loadChat,
    entityEstablished, // NEW
  };
}